import { useTranslate } from '@ur-solutions/react-hooks'
import type { TableMenuItem } from '@/components_deprecated'
import { CenteredLoader, TableMenu } from '@/components_deprecated'
import { Card } from '@/components_deprecated/Card'
import { Vehicles } from '@/components_deprecated/Table/cells'
import type { IssueNodeForAllIssues } from '@/modules/issues/types.graphl'
import React from 'react'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import { removeCurrentYear } from '@/utils/date'
import { format, formatDistanceToNow } from '@/utils/date-fns'
import {
  IssueCategory as BaseCategory,
  IssueStatus as BaseStatus,
} from './components'
import { OverflowTooltip } from '@/components/overflow-tooltip'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  & > div + div {
    margin-top: 1rem;
  }
`
const Issue = styled(Card)`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas:
    'user     status   menu'
    'category category category'
    'date     date     date'
    'vehicle  vehicle  vehicle';
  gap: 6px;

  h3 {
    grid-area: user;
    margin: 0;
  }
  div.menu {
    grid-area: menu;
    margin-left: 1rem;
  }
`
const Status = styled(BaseStatus)`
  grid-area: status;
  align-self: center;
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  margin: 0;
  margin-top: -4px;

  font-weight: 500;
  font-size: 0.8rem;

  &::before {
    margin-left: 0.8ch;
    margin-right: 0;
    margin-bottom: 2px;
  }
`
const Category = styled(BaseCategory)`
  grid-area: category;
  margin: 0 0 6px -6px;

  div {
    padding: 6px 12px 3px;
    font-size: 0.8rem;
  }
`
const DateField = styled.div`
  grid-area: date;

  div {
    font-style: normal;
    font-weight: 600;
    font-size: 0.9rem;
    line-height: 0.8rem;
  }
  span {
    font-size: 0.7em;
    color: ${props => props.theme.colors.gray6};
  }
`
const VehicleAndReference = styled.div`
  grid-area: vehicle;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;

  .ref-stack {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    min-width: 0;
  }

  .title {
    color: ${props => props.theme.colors.text.dark};
    font-weight: 600;
    font-size: 0.9rem;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span.reference {
    em {
      margin-right: 0.5ch;
      font-style: normal;
      color: ${props => props.theme.colors.gray5};
    }
  }
`

interface IssuesCardsProps {
  issues: IssueNodeForAllIssues[]
  queryLoading: boolean
  issueLoading: string | null

  createMenuItems: (
    issue: IssueNodeForAllIssues
  ) => TableMenuItem<IssueNodeForAllIssues>[]

  onLoadMore: () => void
}

export const IssuesCards: React.FC<IssuesCardsProps> = ({
  issues,
  queryLoading,
  issueLoading,

  createMenuItems,
}) => {
  const translations = useTranslate({
    open: 'common.open-alt',
    archived: 'common.archived',
    closed: 'common.closed',
    started: 'issues.started',

    ref: ['issues.reference', { abbreviate: true }, val => val.toLowerCase()],

    editIssue: 'issues.edit-issue',
    closeIssue: 'issues.close-issue',
    reopenIssue: 'issues.reopen-issue',
    archiveIssue: 'issues.archive-issue',
    restoreIssue: 'issues.restore-issue',
  })

  const navigate = useNavigate()

  const handleCardClick = (e: React.MouseEvent, issueId: string) => {
    if ((e.target as HTMLElement).closest('.menu')) {
      return
    }
    void navigate(`/issues/${issueId}`)
  }

  return (
    <Wrapper>
      {queryLoading && <CenteredLoader marginTop="1rem" marginBottom="1rem" />}

      {issues.map(issue => {
        const date = new Date(issue.timeOfIssueStart as string)
        const dateString = removeCurrentYear(format(date, 'PPP'))
        const timeAgo = formatDistanceToNow(date, { addSuffix: true })
        const menuItems = createMenuItems(issue)

        return (
          <Issue key={issue.id} onClick={e => handleCardClick(e, issue.id)}>
            <h3>{issue.user.fullName}</h3>

            <Status
              closed={issue.closed}
              archived={issue.archived}
              openText={translations.open}
              archivedText={translations.archived}
              closedText={translations.closed}
              edited={issue.edited}
              editText={translations.started}
            />

            <div className="menu">
              <TableMenu
                items={menuItems}
                loading={issueLoading === issue.id}
                loaderSize={18}
                loaderThickness={2}
              />
            </div>

            <Category category={issue.category} />

            <DateField>
              <div>{dateString}</div>
              <span>{timeAgo}</span>
            </DateField>

            <VehicleAndReference>
              {!!issue.vehiclePlateNumber && (
                <div>
                  <Vehicles plates={[issue.vehiclePlateNumber ?? '']} />
                </div>
              )}
              <div className="ref-stack">
                {issue.title && (
                  <OverflowTooltip text={issue.title}>
                    <span className="title">{issue.title}</span>
                  </OverflowTooltip>
                )}
                <span className="reference">
                  <em>{translations.ref({ abbreviate: true })}:</em>
                  {issue.referenceId}
                </span>
              </div>
            </VehicleAndReference>
          </Issue>
        )
      })}
    </Wrapper>
  )
}
